/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getStationsActionsLogs as getStationsReportSearchLogs,
  getStationsLogDetails,
} from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  mapRouteToReportTypeLabel,
  mapStationsReportRows,
} from './reportTypeOptions'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import AuditStationsReportFilters from './AuditStationsReportFilters'
import { SearchLogResponse, StationsReportSearchLogRow } from '../interfaces'

const limit = 15

export default function ListAllAuditStationsReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const stationId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<'stations' | 'station' | null>(null)
  const selectedStations = useHookstate<number | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandStationsReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<StationsReportSearchLogRow | null>(null)
  const requestType = useHookstate<string | null>(null)
  const requestRoute = useHookstate<string | null>(null)

  const [loadingRowId, setLoadingRowId] = useState<number | null>(null) // Novo state para loading do row específico
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})

  // mapActionType movida para reportTypeOptions.ts (import acima)
  const columns: readonly GridColDef[] = [
    {
      field: 'station',
      headerName: translate('Estação'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'route',
      headerName: translate('action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (value, row) =>
        mapRouteToReportTypeLabel(translate, value as string, row.request_type),
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip
            title={translate('Visualizar')}
            open={
              openTooltips[
                String((params.row as StationsReportSearchLogRow).id)
              ] ?? false
            }
            onOpen={() =>
              setOpenTooltips((prev) => ({
                ...prev,
                [String((params.row as StationsReportSearchLogRow).id)]: true,
              }))
            }
            onClose={() =>
              setOpenTooltips((prev) => ({
                ...prev,
                [String((params.row as StationsReportSearchLogRow).id)]: false,
              }))
            }
          >
            <IconButton
              onClick={() => {
                setOpenTooltips((prev) => ({
                  ...prev,
                  [String((params.row as StationsReportSearchLogRow).id)]:
                    false,
                })) // Força o fechamento do tooltip ao clicar
                handleView(params.row as StationsReportSearchLogRow)
              }}
              size="small"
              aria-label={translate('Visualizar')}
              disabled={
                loadingRowId === (params.row as StationsReportSearchLogRow).id
              }
            >
              {loadingRowId ===
              (params.row as StationsReportSearchLogRow).id ? (
                <CircularProgress size={20} />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    stationId.set(null)
    action.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandStationsReports.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: SearchLogResponse = await getStationsReportSearchLogs({
      station_id: stationId.get() || undefined,
      start_date: startDate,
      finish_date: finishDate,
      action: action.get(),
      page: page,
      limit: pageSize,
      orders:
        orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
    }).finally(() => {
      loadingButton.set(false)
      expandStationsReports.set(true)
    })

    // Usar a função dedicada para mapear as linhas
    setRows(
      mapStationsReportRows(searchLogData.data, page, pageSize, translate)
    )

    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const handleView = async (row: StationsReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }
      requestType.set(row.request_type ?? null)
      requestRoute.set(row.route ?? null)
      const details = await getStationsLogDetails(Number(row.id))
      setSelectedRow(details as StationsReportSearchLogRow)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditStationsReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          stationId={stationId}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          originType={originType}
          selectedDevice={selectedStations}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() =>
                expandStationsReports.set(!expandStationsReports.get())
              }
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandStationsReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandStationsReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={(event?: unknown, reason?: string) => {
          if (reason !== 'backdropClick') {
            setOpenModal(false)
          }
        }}
        selectedRow={selectedRow}
        fieldsToDisplay={[
          { label: 'Nome', value: selectedRow?.name ?? 'Não informado' },
          {
            label: 'Linha de Filial',
            value: selectedRow?.branch_line ?? 'Não informado',
          },
          {
            label: 'Serial HD',
            value: selectedRow?.serial_hd ?? 'Não informado',
          },
          {
            label: 'Endereço MAC',
            value: selectedRow?.mac_address ?? 'Não informado',
          },
          {
            label: 'Agência',
            value: selectedRow?.agency?.name ?? 'Não informado',
          },
          {
            label: 'Agência (acrônimo)',
            value: selectedRow?.agency?.acronym ?? 'Não informado',
          },
          {
            label: 'Centro de Contato',
            value: selectedRow?.contact_center?.name ?? 'Não informado',
          },
          {
            label: translate('Performed at') || 'Realizado em',
            value: selectedRow?.created_at
              ? dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')
              : 'Não informado',
          },
          {
            label: 'Grupos de Despacho',
            value: selectedRow?.dispatch_groups
              ? selectedRow.dispatch_groups.map((g) => g.name).join(', ')
              : 'Não informado',
            size: 12,
          },
        ]}
      />
    </>
  )
}
